import { useQuery } from "@tanstack/react-query";
import React, { useContext, useState } from "react";
import toast from "react-hot-toast";
import { TypeAnimation } from "react-type-animation";
import { ScaleLoader } from "react-spinners";
import { useForm } from "react-hook-form";
import { AuthContext } from "../../../contexts/AuthProvider";

const BookingModal = ({ phone, setPhone }) => {
  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm();

  const {
    _id,
    productName,
    category,
    resalePrice,
    productImg,
    sellerEmail,
    sellerName,
  } = phone;

  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);

  const bookingDate = new Date().toLocaleDateString();
  const bookingTime = new Date().toLocaleTimeString();

  const { data: users = [], refetch } = useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      const res = await fetch("https://recycle-hut-server.vercel.app/users");
      const data = await res.json();
      return data;
    },
  });

  const currentUser = users.find((u) => u?.email === user?.email);

  const handleBooking = (event, data) => {
    const form = event.target;

    const phone = data.phone;
    const meetLocation = data.meetLocation;

    if (currentUser?.role === "buyer") {
      const booking = {
        productId: _id,
        mobileName: productName,
        img: productImg,
        category,
        price: resalePrice,
        sellerName,
        sellerEmail,
        buyerName: user.displayName,
        buyerEmail: user.email,
        buyerMobile: phone,
        meetLocation,
        bookingDate,
        bookingTime,
      };

      setLoading(true);
      fetch("https://recycle-hut-server.vercel.app/bookings", {
        method: "POST",
        headers: {
          "content-type": "application/json",
        },
        body: JSON.stringify(booking),
      })
        .then((res) => res.json())
        .then((data) => {
          // console.log(data);
          if (data.acknowledged) {
            setPhone(null);
            toast.success(`"${productName}" Booked successfully..`);

            setBookingStatus(_id, productName);

            refetch();
            setLoading(false);
          }
        });
    } else {
      toast.error(
        "Only Buyers can book phones. Please create a 'Buyer' account first!"
      );
      form.reset();
      return;
    }
  };

  const setBookingStatus = (productId) => {
    fetch(
      `https://recycle-hut-server.vercel.app/products/bookingstatus-true/${productId}`,
      {
        method: "PUT",
      }
    )
      .then((res) => res.json())
      .then((data) => {
        if (data.modifiedCount > 0) {
          refetch();
        }
      });
  };

  return (
    <div className="rounded">
      <input type="checkbox" id="booking-modal" className="modal-toggle" />
      <div className="modal">
        <div className="modal-box relative items-center lg:p-[20px]">
          <div className="mb-[-20px]">
            <label
              htmlFor="booking-modal"
              className="btn btn-sm btn-circle absolute right-3 md:mt-[3px] mr-2 hover:text-white hover:bg-red-400 hover:border-none"
              title="Close"
            >
              ✕
            </label>

            <h3 className="text-[22px] md:text-[24px] text-cyan-500 font-bold mt-0">
              <TypeAnimation
                sequence={["You are ready to Book", 3000]}
                speed={50}
                cursor={true}
                repeat={Infinity}
              />
            </h3>
          </div>

          <form
            onSubmit={handleSubmit(handleBooking)}
            className="grid grid-cols-1 gap-3 mt-[33px] md:mt-[38px]"
          >
            <div className="flex flex-col md:flex-row gap-1 md:gap-4">
              <div className="form-control w-full">
                <label className="label">
                  <span className="label-text font-semibold">Phone Name</span>
                </label>

                <input
                  type="text"
                  disabled
                  defaultValue={productName}
                  className="input input-bordered w-full"
                />
              </div>

              <div className="form-control w-full">
                <label className="label">
                  <span className="label-text font-semibold">Price (BDT)</span>
                </label>
                <input
                  type="text"
                  disabled
                  defaultValue={resalePrice}
                  className="input w-full input-bordered"
                />
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-1 md:gap-4 mt-[-8px] md:mt-[0px]">
              <div className="form-control w-full">
                <label className="label">
                  <span className="label-text font-semibold">Booking Date</span>
                </label>
                <input
                  type="text"
                  defaultValue={bookingDate}
                  disabled
                  placeholder="Booking Date"
                  className="input w-full input-bordered"
                />
              </div>

              <div className="form-control w-full">
                <label className="label">
                  <span className="label-text font-semibold">Booking Time</span>
                </label>
                <input
                  type="text"
                  defaultValue={bookingTime}
                  disabled
                  placeholder="Booking Time"
                  className="input w-full input-bordered"
                />
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-1 md:gap-4 mt-[-8px] md:mt-[0px]">
              <div className="form-control w-full">
                <label className="label">
                  <span className="label-text font-semibold">Your Name</span>
                </label>
                <input
                  type="text"
                  defaultValue={user?.displayName}
                  disabled
                  placeholder="Name"
                  className="input w-full input-bordered"
                />
              </div>

              <div className="form-control w-full">
                <label className="label">
                  <span className="label-text font-semibold">Your Email</span>
                </label>
                <input
                  type="email"
                  defaultValue={user?.email}
                  disabled
                  placeholder="Email Address"
                  className="input w-full input-bordered"
                />
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-1 md:gap-4 mt-[-8px] md:mt-[0px]">
              <div className="form-control w-full">
                <label className="label">
                  <span className="label-text font-semibold">Phone Number</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter Number"
                  {...register("phone", {
                    required: "Phone Number is Required",
                  })}
                  className="input w-full input-bordered"
                />
                {errors.phone && (
                  <p className="text-error font-bold text-start mt-2">
                    {errors.phone?.message}
                  </p>
                )}
              </div>

              <div className="form-control w-full">
                <label className="label">
                  <span className="label-text font-semibold">
                    Your Location
                  </span>
                </label>
                <input
                  type="text"
                  placeholder="Enter Location"
                  {...register("meetLocation", {
                    required: "Your Location is Required",
                  })}
                  className="input w-full input-bordered text-m"
                />
                {errors.meetLocation && (
                  <p className="text-error font-bold text-start mt-2">
                    {errors.meetLocation?.message}
                  </p>
                )}
              </div>
            </div>

            <br />

            <button
              className="btn btn-accent bg-cyan-500 text-white w-full mt-[-33px]"
              value="Submit To Book"
            >
              {loading ? (
                <ScaleLoader color="#fff" height={16} />
              ) : (
                "Submit To Book"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BookingModal;
